import { filter, merge, mergeMap, pipe, takeUntil, onPush, share } from "wonka";
import {
  makeFetchBody,
  makeFetchURL,
  makeFetchOptions,
  makeFetchSource,
} from "@urql/core/internal";
// Workaround to ensure that graphql-query-compress' built-in packages are included in SSR
// https://github.com/rse/graphql-query-compress/issues/10
import compress from "graphql-query-compress/lib/graphql-query-compress.browser.js";
/** Custom GraphQL over HTTP fetch exchange.
 *
 * @remarks
 * The default fetch exchange in `urql` supports sending GraphQL over HTTP
 * requests. It 'stringifies' the Graphql query before sending across network.
 *
 * In order to compress the Graphql query request payload, we have to create a
 * custom fetch exchange incorporating the query compression.
 * https://github.com/urql-graphql/urql/issues/3073
 *
 * Below custom fetch exchange is created by extending the default fetch exchange in @urql/core@2.6.1
 * https://github.com/urql-graphql/urql/blob/%40urql/core%402.6.1/packages/core/src/exchanges/fetch.ts
 */
const compressQuery = (body) => {
  if (!!body.query) {
    body.query = compress(body.query);
  }
  return body;
};

export const fetchExchange = ({ forward, dispatchDebug }) => {
  return (ops) => {
    const sharedOps = share(ops);
    const fetchResults = pipe(
      sharedOps, // value passed as an argument
      filter((operation) => {
        return operation.kind === "query" || operation.kind === "mutation";
      }), // return value passed to mergeMap
      mergeMap((operation) => {
        const { key } = operation;
        const teardown = pipe(
          sharedOps,
          filter((op) => op.kind === "teardown" && op.key === key) // sharedOps is passed as arg
        );

        const body = makeFetchBody(operation);
        compressQuery(body);
        const url = makeFetchURL(operation, body);
        const fetchOptions = makeFetchOptions(operation, body);

        dispatchDebug({
          type: "fetchRequest",
          message: "A fetch request is being executed.",
          operation,
          data: {
            url,
            fetchOptions,
          },
        });

        return pipe(
          makeFetchSource(operation, url, fetchOptions),
          takeUntil(teardown),
          onPush((result) => {
            const error = !result.data ? result.error : undefined;

            dispatchDebug({
              type: error ? "fetchError" : "fetchSuccess",
              message: `A ${
                error ? "failed" : "successful"
              } fetch response has been returned.`,
              operation,
              data: {
                url,
                fetchOptions,
                value: error || result,
              },
            });
          })
        );
      })
    );
    const newForward = pipe(
      sharedOps,
      filter((operation) => {
        return operation.kind !== "query" && operation.kind !== "mutation";
      }),
      forward
    );
    return merge([fetchResults, newForward]);
  };
};
